import {DecimalPipe} from '@angular/common'
import {Component, computed, inject, input, OnInit, signal} from '@angular/core'
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion'
import {MatIcon} from '@angular/material/icon'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter} from 'rxjs/operators'
import {KalpBuilding} from '../../model/kalp-building'
import {KalpLoan} from '../../model/kalp-loan'
import {KalpResult} from '../../model/kalp-result'
import {getEmptyLegacyKalp, KalpService, LegacyKalp, Scenarios, TLegacyKalps} from '../../services/kalp.service'

@Component({
  selector: 'spb-kalp-display',
  templateUrl: './kalp-display.component.html',
  styleUrls: ['./kalp-display.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatExpansionPanelContent,
    MatIcon,
    DecimalPipe
  ]
})

export class KalpDisplayComponent implements OnInit {

  public readonly kalp = input<LegacyKalp>(getEmptyLegacyKalp())

  /**
   * Which of the three KALPS?
   */
  public readonly index = input(0)

  public scenario = signal<Scenarios>(Scenarios.Purchase)

  public kalpResult = signal<KalpResult>(new KalpResult())

  public kalpService = inject(KalpService)

  public includesBlanco = signal(false)

  private readonly allLoans = signal<KalpLoan[]>([])

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  public monthlyBuildingCost = computed(() => {
    return this.kalp().buildings.reduce((sum: number, building: KalpBuilding) => sum + building.monthlyCost, 0)
  })

  public runCost = computed(() => {
    return this.kalp().buildings
      .reduce((acc: number, b: KalpBuilding) => acc + b.runCost, 0)
  })

  public propertyTax = computed(() => {
    return this.kalp().buildings
      .reduce((acc: number, b: KalpBuilding) => acc + b.propertyTax, 0)
  })

  public fee = computed(() => {
    return this.kalp().buildings
      .reduce((acc: number, b: KalpBuilding) => acc + b.fee, 0)
  })

  public propertySum = computed(() => {
    return this.kalp().buildings
      .reduce((acc: number, b: KalpBuilding) => acc + b.monthlyCost, 0)
  })

  /**
   * Used to tell if the new / solve columns should be shown
   */
  public showCol = computed(() => {
    return this.scenario() !== Scenarios.Conditions
  })

  public ngOnInit(): void {
    this.kalpService.scenarioChange$
      .pipe(
        this.takeUntilDestroyed(),
        filter(Boolean)
      )
      .subscribe(scenario => this.scenario.set(scenario as Scenarios))

    this.kalpService.kalpResult$
      .pipe(this.takeUntilDestroyed())
      .subscribe(((kalps: TLegacyKalps) => {
        this.kalpResult.set(new KalpResult(kalps[this.index()]))
      }))

    this.kalpService.loans$
      .pipe(this.takeUntilDestroyed())
      .subscribe((loans: KalpLoan[]) => {
        this.allLoans.set(loans)
        this.includesBlanco.set(loans.some(l => l.isBlancoLoan))
      })
  }


  public getBuildings(): KalpBuilding[] {
    return this.kalp().buildings
  }

  public getMortgageSum(): number {
    return this.kalp().loans.loans
      .filter((loan: KalpLoan) => !loan.solve || this.index() === 0)
      .reduce((acc, val) => acc + val.mortgageAmount, 0)
  }

  public getInterestSum(): number {
    return this.kalp().loans.loans
      .filter((loan: KalpLoan) => !loan.solve || this.index() === 0)
      .reduce((acc, val) => acc + val.interestAmount, 0)
  }

  public getMonthlyLoanCost(): number {
    return this.getInterestSum() + this.getMortgageSum()
  }

  /**
   * Only used for display, we display all loans
   * even if they are not in the KALP, strange.
   */
  public getLoans(): KalpLoan[] {
    const loans = this.allLoans()
      .filter((loan: KalpLoan) => {
        /**
         * If scenario Conditions we return ??
         */
        if (this.scenario() === Scenarios.Conditions) {
          if (this.index() !== 0 && loan.solve) {
            return false
          }
        }
        // Skip "Before change" KALP we remove all new loans
        return !(this.index() === 0 && loan.new)
      })
    return this.arrangeLoans(loans)
  }

  public arrangeLoans(loans: KalpLoan[]): KalpLoan[] {
    const blanco = this.sortSortLoans(loans.filter(loan => loan.isBlancoLoan))
    const keepers = this.sortSortLoans(loans.filter(loan => !loan.solve && !loan.new && !loan.isBlancoLoan))
    const solvers = this.sortSortLoans(loans.filter(loan => loan.solve && !loan.isBlancoLoan))
    const newLoans = this.sortSortLoans(loans.filter(loan => loan.new))
    return blanco.concat(keepers, solvers, newLoans)
  }

  public sortSortLoans(loans: KalpLoan[]): KalpLoan[] {
    return loans.toSorted((a: KalpLoan, b: KalpLoan): number => {
      return b.amount - a.amount
    })
  }
}
